/**
 * TargetTableNode — Custom ReactFlow node for an individual target table.
 *
 * Shown when there are ≤50 target tables (above that, TargetGroupNode is used).
 * Compact card with teal left border, table icon, name, and materialization status.
 * Top Handle (mapping_output → target table) + Bottom Handle (target table → downstream transforms).
 */
import React from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Box, Chip, Tooltip, Typography, useTheme, alpha } from '@mui/material';
import {
  TableChart as TargetIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Sync as RunningIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';

import { ReadinessChip } from './readiness/ReadinessChip';
import type { ReadinessStatus } from '../../types/readiness';

export interface TargetTableNodeData {
  tableName: string;
  status: 'completed' | 'failed' | 'pending' | 'running' | 'awaiting_review';
  description?: string;
  error?: string;
  /** Number of health issues from mapping health API (legacy). */
  healthIssueCount?: number;
  /** Worst severity among health issues (legacy). */
  healthSeverity?: 'error' | 'warning' | null;
  /** Readiness rollout — populated by the workspace page from one
   *  consolidated /readiness fetch. When present, the Readiness chip
   *  replaces the legacy health chip on this node. */
  readinessStatus?: ReadinessStatus;
  /** Count to render on the chip (blockers when not_ready,
   *  advisories when ready_with_notes). */
  readinessFindingCount?: number;
  /** Live state from the unified progress channel. When populated,
   *  the node renders a small progress badge (queued / running /
   *  done / needs_input / failed) over its top-right. The status
   *  field above is the persisted post-run state — progressState is
   *  the in-flight signal during an active engine/iterative run. */
  progressState?: import('../../types/progress').TaskState;
  /** Ephemeral activity string from the progress channel
   *  (e.g. "Validating join keys"). Shown on hover. */
  progressActivity?: string | null;
}

const STATUS_CONFIG: Record<string, { icon: typeof SuccessIcon; color: string }> = {
  completed: { icon: SuccessIcon, color: '#22c55e' },
  failed: { icon: ErrorIcon, color: '#ef4444' },
  running: { icon: RunningIcon, color: '#3b82f6' },
  pending: { icon: PendingIcon, color: '#94a3b8' },
  awaiting_review: { icon: PendingIcon, color: '#f59e0b' },
};

const TargetTableNode: React.FC<NodeProps<TargetTableNodeData>> = ({
  data,
  selected,
  sourcePosition,
  targetPosition,
}) => {
  const targetHandlePos = targetPosition ?? Position.Top;
  const sourceHandlePos = sourcePosition ?? Position.Bottom;
  const theme = useTheme();
  const {
    tableName, status, description, error,
    healthIssueCount, healthSeverity,
    readinessStatus, readinessFindingCount,
    progressState, progressActivity,
  } = data;

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const StatusIcon = config.icon;
  const borderColor = status === 'failed' ? '#ef4444' : theme.palette.info.main;

  return (
    <Box
      sx={{
        width: 180,
        background: '#f8fafe',
        borderRadius: 0.5,
        border: selected
          ? `2px solid ${borderColor}`
          : '1.5px solid #e2e8f0',
        borderLeft: `3px solid ${borderColor}`,
        boxShadow: selected
          ? `0 0 0 3px ${alpha(borderColor, 0.15)}`
          : '0 1px 2px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        cursor: 'pointer',
        px: 1.25,
        py: 1,
      }}
    >
      {/* Target handle — incoming edge */}
      <Handle
        type="target"
        position={targetHandlePos}
        style={{
          background: borderColor,
          width: 7,
          height: 7,
          border: `2px solid ${alpha(borderColor, 0.3)}`,
        }}
      />
      {/* Source handle — outgoing edge to downstream */}
      <Handle
        type="source"
        position={sourceHandlePos}
        style={{
          background: borderColor,
          width: 7,
          height: 7,
          border: `2px solid ${alpha(borderColor, 0.3)}`,
        }}
      />

      {/* Content row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <TargetIcon sx={{ fontSize: 16, color: borderColor, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{
              fontSize: 11,
              fontFamily: 'monospace',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {tableName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: status === 'failed' ? '#ef4444' : '#94a3b8',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {error ? error.substring(0, 40) : (description || 'Target table')}
          </Typography>
        </Box>
        {/* Live progress badge from the unified progress channel.
            Renders only during an active run; once the run completes,
            the underlying ``status`` field carries the persisted state
            and this badge melts away. */}
        {progressState && progressState !== 'queued' && (
          <Tooltip title={progressActivity || progressState} placement="top" arrow>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: (() => {
                  switch (progressState) {
                    case 'running': return theme.palette.primary.main;
                    case 'done': return theme.palette.success.main;
                    case 'needs_input': return theme.palette.warning.main;
                    case 'failed': return theme.palette.error.main;
                    default: return theme.palette.text.disabled;
                  }
                })(),
                animation: progressState === 'running' ? 'progress-node-pulse 1.6s ease-in-out infinite' : 'none',
                '@keyframes progress-node-pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
          </Tooltip>
        )}
        <StatusIcon sx={{ fontSize: 14, color: config.color, flexShrink: 0 }} />
        {/* Readiness chip (new) — preferred when populated. */}
        {readinessStatus && (
          <Box sx={{ flexShrink: 0, transform: 'scale(0.75)', transformOrigin: 'right center' }}>
            <ReadinessChip
              state={readinessStatus}
              count={readinessFindingCount ?? 0}
              size="small"
            />
          </Box>
        )}
        {/* Legacy health chip — kept as fallback when readinessStatus
            hasn't been populated yet by the workspace page's /readiness
            fetch. Removed once that wiring is everywhere. */}
        {!readinessStatus && !!healthIssueCount && healthIssueCount > 0 && (
          <Tooltip title={`${healthIssueCount} validation issue${healthIssueCount !== 1 ? 's' : ''}`} placement="top" arrow>
            <Chip
              size="small"
              label={healthIssueCount}
              icon={healthSeverity === 'error'
                ? <ErrorIcon sx={{ fontSize: '10px !important' }} />
                : <WarningIcon sx={{ fontSize: '10px !important' }} />}
              sx={{
                height: 16,
                fontSize: 9,
                fontWeight: 700,
                flexShrink: 0,
                bgcolor: alpha(healthSeverity === 'error' ? '#ef4444' : '#f59e0b', 0.12),
                color: healthSeverity === 'error' ? '#ef4444' : '#f59e0b',
                border: `1px solid ${alpha(healthSeverity === 'error' ? '#ef4444' : '#f59e0b', 0.3)}`,
                '& .MuiChip-icon': { ml: 0.25, mr: -0.25 },
                '& .MuiChip-label': { px: 0.4 },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default TargetTableNode;
